import { useEffect, useState } from 'react';
import { DashboardLayout } from '../../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Calendar } from '../../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../../components/ui/popover';
import { Checkbox } from '../../components/ui/checkbox';
import { Badge } from '../../components/ui/badge';
import { format } from 'date-fns';
import api from '../../services/api';
import { toast } from 'sonner';
import { CalendarIcon, ClipboardCheck, Users, PauseCircle } from 'lucide-react';
import { useBusinessTimezone, getTodayInTz, parseDateOnly } from '../../utils/businessTime';

const Attendance = () => {
  const businessTz = useBusinessTimezone();
  const [classes, setClasses] = useState([]);
  const [selectedClass, setSelectedClass] = useState('');
  // selectedDate uses business-TZ "today" so coaches in another TZ
  // don't open the attendance form on the wrong day.
  const [selectedDate, setSelectedDate] = useState(() => parseDateOnly(getTodayInTz('America/New_York')));
  const [hasNavigated, setHasNavigated] = useState(false);
  useEffect(() => {
    if (!businessTz || hasNavigated) return;
    setSelectedDate(parseDateOnly(getTodayInTz(businessTz)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [businessTz]);
  const [players, setPlayers] = useState([]);
  const [attendance, setAttendance] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const fetchClasses = async () => {
      try {
        const response = await api.get('/dashboard/stats');
        setClasses(response.data.classes || []);
      } catch (error) {
        toast.error('Failed to fetch classes');
      } finally {
        setLoading(false);
      }
    };
    fetchClasses();
  }, []);

  const fetchPlayersAndAttendance = async () => {
    if (!selectedClass) return;
    
    try {
      const [playersRes, attendanceRes] = await Promise.all([
        api.get(`/classes/${selectedClass}/enrollments?include_paused=true`),
        api.get(`/attendance?class_id=${selectedClass}&date=${format(selectedDate, 'yyyy-MM-dd')}`)
      ]);
      
      // Map enrollments to player format expected by attendance
      // Include both active and paused players with status indicators
      const playersList = playersRes.data
        .filter(enrollment => {
          // Include active and paused enrollments
          const isActive = enrollment.enrollment_status === 'active' || 
                          enrollment.enrollment_status === 'current' ||
                          enrollment.enrollment_status === 'paused';
          return isActive;
        })
        .map(enrollment => ({
          id: enrollment.player_id,
          full_name: enrollment.player_name,
          name: enrollment.player_name,
          enrollment_status: enrollment.enrollment_status,
          pause_date: enrollment.pause_date,
          auto_resume_date: enrollment.auto_resume_date,
          days_until_resume: enrollment.days_until_resume,
          scheduled_cancel_date: enrollment.scheduled_cancel_date
        }));
      setPlayers(playersList);
      
      // Build attendance state from existing records
      const attendanceMap = {};
      attendanceRes.data.forEach(record => {
        attendanceMap[record.player_id] = record.status === 'present';
      });
      setAttendance(attendanceMap);
    } catch (error) {
      console.error('Attendance fetch error:', error);
      toast.error('Failed to fetch attendance data');
    }
  };

  useEffect(() => {
    fetchPlayersAndAttendance();
  }, [selectedClass, selectedDate]);

  const handleAttendanceChange = (playerId, present) => {
    setAttendance(prev => ({ ...prev, [playerId]: present }));
  };

  const handleSaveAttendance = async () => {
    if (!selectedClass) {
      toast.error('Please select a class');
      return;
    }

    setSaving(true);
    try {
      const dateStr = format(selectedDate, 'yyyy-MM-dd');
      
      // Save attendance for each player
      for (const player of players) {
        await api.post('/attendance', {
          class_id: selectedClass,
          player_id: player.id,
          date: dateStr,
          status: attendance[player.id] ? 'present' : 'absent'
        });
      }
      
      toast.success('Attendance saved successfully');
    } catch (error) {
      console.error('Attendance save error:', error);
      toast.error('Failed to save attendance');
    } finally {
      setSaving(false);
    }
  };

  const presentCount = Object.values(attendance).filter(Boolean).length;
  const absentCount = players.length - presentCount;

  if (loading) {
    return (
      <DashboardLayout title="Attendance">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-500 border-t-transparent"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title="Attendance">
      <div className="space-y-6" data-testid="attendance-page">
        <p className="text-slate-600 text-sm lg:text-base">Mark and track student attendance</p>

        {/* Filters */}
        <Card className="border border-slate-200">
          <CardContent className="p-4 lg:p-6">
            <div className="flex flex-col sm:flex-row gap-3 lg:gap-4">
              <div className="flex-1">
                <label className="text-sm font-medium text-slate-700 mb-2 block">Select Class</label>
                <Select value={selectedClass} onValueChange={setSelectedClass}>
                  <SelectTrigger data-testid="attendance-class-select">
                    <SelectValue placeholder="Choose a class" />
                  </SelectTrigger>
                  <SelectContent>
                    {classes.map((classItem) => (
                      <SelectItem key={classItem.id} value={classItem.id}>
                        {classItem.name} - {classItem.schedule_day}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="flex-1">
                <label className="text-sm font-medium text-slate-700 mb-2 block">Select Date</label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button 
                      variant="outline" 
                      className="w-full justify-start text-left font-normal"
                      data-testid="attendance-date-btn"
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {format(selectedDate, 'PPP')}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={selectedDate}
                      onSelect={(date) => { if (date) { setHasNavigated(true); setSelectedDate(date); } }}
                      disabled={(date) => date > parseDateOnly(getTodayInTz(businessTz))}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
                <p className="text-xs text-slate-500 mt-1">Future dates are disabled</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {selectedClass && (
          <>
            {/* Stats */}
            <div className="grid grid-cols-3 gap-2 lg:gap-4">
              <Card className="border border-emerald-200 bg-emerald-50">
                <CardContent className="p-3 lg:p-4 flex items-center gap-2 lg:gap-3">
                  <div className="p-1.5 lg:p-2 bg-blue-500 rounded-lg">
                    <Users className="h-4 w-4 lg:h-5 lg:w-5 text-white" />
                  </div>
                  <div>
                    <p className="text-xs lg:text-sm text-blue-700">Present</p>
                    <p className="font-heading text-xl lg:text-2xl font-bold text-emerald-900">{presentCount}</p>
                  </div>
                </CardContent>
              </Card>
              
              <Card className="border border-rose-200 bg-rose-50">
                <CardContent className="p-3 lg:p-4 flex items-center gap-2 lg:gap-3">
                  <div className="p-1.5 lg:p-2 bg-rose-500 rounded-lg">
                    <Users className="h-4 w-4 lg:h-5 lg:w-5 text-white" />
                  </div>
                  <div>
                    <p className="text-xs lg:text-sm text-rose-700">Absent</p>
                    <p className="font-heading text-xl lg:text-2xl font-bold text-rose-900">{absentCount}</p>
                  </div>
                </CardContent>
              </Card>

              <Card className="border border-amber-200 bg-amber-50">
                <CardContent className="p-3 lg:p-4 flex items-center gap-2 lg:gap-3">
                  <div className="p-1.5 lg:p-2 bg-amber-500 rounded-lg">
                    <PauseCircle className="h-4 w-4 lg:h-5 lg:w-5 text-white" />
                  </div>
                  <div>
                    <p className="text-xs lg:text-sm text-amber-700">Paused</p>
                    <p className="font-heading text-xl lg:text-2xl font-bold text-amber-900">
                      {players.filter(p => p.enrollment_status === 'paused').length}
                    </p>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Attendance List */}
            <Card className="border border-slate-200">
              <CardHeader className="border-b border-slate-100">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                  <CardTitle className="font-heading text-xl">Student List</CardTitle>
                  <Button 
                    onClick={handleSaveAttendance}
                    disabled={saving || players.length === 0}
                    className="bg-blue-500 hover:bg-blue-600"
                    data-testid="save-attendance-btn"
                  >
                    {saving ? (
                      <div className="flex items-center gap-2">
                        <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                        Saving...
                      </div>
                    ) : (
                      <>
                        <ClipboardCheck className="h-4 w-4 mr-2" />
                        Save Attendance
                      </>
                    )}
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="p-0">
                {players.length === 0 ? (
                  <div className="p-12 text-center">
                    <Users className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                    <p className="text-slate-500">No students enrolled in this class</p>
                  </div>
                ) : (
                  <div className="divide-y divide-slate-100">
                    {players.map((player) => (
                      <div 
                        key={player.id}
                        className={`p-3 lg:p-4 flex items-center justify-between hover:bg-slate-50 transition-colors cursor-pointer ${
                          player.enrollment_status === 'paused' ? 'bg-amber-50/50' : ''
                        }`}
                        data-testid={`attendance-player-${player.id}`}
                        onClick={() => handleAttendanceChange(player.id, !attendance[player.id])}
                      >
                        <div className="flex items-center gap-2 lg:gap-3 flex-1 min-w-0">
                          <div className={`w-8 h-8 lg:w-10 lg:h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                            player.enrollment_status === 'paused' 
                              ? 'bg-amber-100' 
                              : 'bg-blue-100'
                          }`}>
                            <span className={`font-semibold text-sm lg:text-base ${
                              player.enrollment_status === 'paused' 
                                ? 'text-amber-600' 
                                : 'text-blue-600'
                            }`}>
                              {player.name?.charAt(0).toUpperCase()}
                            </span>
                          </div>
                          <div className="min-w-0">
                            <div className="flex items-center gap-1.5 lg:gap-2 flex-wrap">
                              <p className="font-medium text-slate-900 text-sm lg:text-base truncate">{player.name}</p>
                              {player.enrollment_status === 'paused' ? (
                                <Badge variant="outline" className="bg-amber-100 text-amber-700 border-amber-300 text-[10px] lg:text-xs">
                                  <PauseCircle className="h-2.5 w-2.5 lg:h-3 lg:w-3 mr-0.5" />
                                  Paused
                                </Badge>
                              ) : (
                                <Badge variant="outline" className="bg-emerald-100 text-emerald-700 border-emerald-300 text-[10px] lg:text-xs hidden lg:inline-flex">
                                  Active
                                </Badge>
                              )}
                              {player.scheduled_cancel_date && (
                                <Badge variant="outline" className="bg-rose-100 text-rose-700 border-rose-300 text-[10px] lg:text-xs hidden lg:inline-flex">
                                  Cancels {player.scheduled_cancel_date}
                                </Badge>
                              )}
                            </div>
                            {player.enrollment_status === 'paused' && player.auto_resume_date && (
                              <p className="text-[10px] lg:text-xs text-amber-600 mt-0.5">
                                Resumes: {player.auto_resume_date} 
                                {player.days_until_resume !== null && (
                                  <span className="text-slate-500">
                                    {player.days_until_resume === 0 
                                      ? ' (today)' 
                                      : player.days_until_resume === 1 
                                        ? ' (tomorrow)' 
                                        : ` (in ${player.days_until_resume} days)`}
                                  </span>
                                )}
                              </p>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-2 lg:gap-4 flex-shrink-0" onClick={(e) => e.stopPropagation()}>
                          <label className="flex items-center gap-1.5 lg:gap-2 cursor-pointer">
                            <Checkbox
                              checked={attendance[player.id] || false}
                              onCheckedChange={(checked) => handleAttendanceChange(player.id, checked)}
                              className="h-5 w-5 lg:h-4 lg:w-4"
                              data-testid={`attendance-checkbox-${player.id}`}
                            />
                            <span className={`text-xs lg:text-sm font-medium ${
                              attendance[player.id] ? 'text-blue-600' : 'text-slate-400'
                            }`}>
                              {attendance[player.id] ? 'Present' : 'Absent'}
                            </span>
                          </label>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </>
        )}
      </div>
    </DashboardLayout>
  );
};

export default Attendance;
